import { GetLeadByExternalId200 } from '../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { LeadDetailStatusEnumKey } from '../../../../services/marketplace/generated/kubb/models/LeadDetail';
import { isParkedPath } from './guidance';

export type ListingStatus =
  | 'building'
  | 'almostLive'
  | 'active'
  | 'accepted'
  | 'sold'
  | 'ended'
  | 'parked';

export type LeadStatusSource =
  | Pick<GetLeadByExternalId200, 'status' | 'gateDecision'>
  | null
  | undefined;

export const LISTING_STATUS_MAP: Record<
  LeadDetailStatusEnumKey,
  Exclude<ListingStatus, 'parked'>
> = {
  new: 'building',
  ready_to_list: 'almostLive',
  active: 'active',
  accepted: 'accepted',
  confirmed: 'accepted',
  contracts_sent: 'accepted',
  awaiting_pickup: 'accepted',
  picked_up: 'accepted',
  billed: 'accepted',
  sold: 'sold',
  fallen: 'ended',
  unavailable: 'ended',
  unresponsive: 'ended',
  expired: 'ended',
};

const deriveBaseListingStatus = (status: LeadDetailStatusEnumKey | undefined): ListingStatus =>
  status != null ? LISTING_STATUS_MAP[status] : 'building';

// ready_to_list parks predate the unavailable-based park and stay valid during rollout.
export const deriveListingStatus = (lead: LeadStatusSource): ListingStatus => {
  if (lead == null) return 'building';
  const isParkable = lead.status === 'unavailable' || lead.status === 'ready_to_list';
  if (isParkable && isParkedPath(lead.gateDecision)) return 'parked';
  return LISTING_STATUS_MAP[lead.status];
};

export const resolveListingStatus = (
  lead: LeadStatusSource,
  almostLiveViewsEnabled: boolean,
): ListingStatus => {
  const derived = deriveListingStatus(lead);
  if (almostLiveViewsEnabled) return derived;
  const unparked = derived === 'parked' ? deriveBaseListingStatus(lead?.status) : derived;
  return unparked === 'almostLive' ? 'active' : unparked;
};

const ON_MARKETPLACE_PATH: ListingStatus[] = ['almostLive', 'active', 'accepted', 'parked'];

export const isLeadOnMarketplacePath = (lead: LeadStatusSource): boolean =>
  ON_MARKETPLACE_PATH.includes(deriveListingStatus(lead));

export const DELIST_HANDOFF_WINDOW_MS = 60_000;

export const isRecentKeepCarDelist = (lead?: GetLeadByExternalId200 | null): boolean =>
  lead?.status === 'unavailable' &&
  lead.delistReason === 'keep_car_buyout' &&
  lead.delistedAt != null &&
  Date.now() - new Date(lead.delistedAt).getTime() < DELIST_HANDOFF_WINDOW_MS;
